import type { TFunction } from "i18next";

import { AtLg, BelowLg } from "@/components/breakpoint";
import type { Raffle } from "./types";
import { rafflePhase } from "./types";
import { optInRows, registerCampaignType } from "../../registry";
import { canJoinCampaign } from "../../can-join";
import { campaignKey } from "../../campaign-key";
import type { CampaignTypeModule, CurrentCampaign } from "../../registry";
import type { AppStore } from "@/stores/app.store";
import { selectRaffleById } from "@/stores/raffle.store";
import { selectJoined } from "@/stores/campaigns.store";
import {
  isActiveCampaignStatus,
  isFinishedCampaignStatus,
} from "../../campaign-status";
import { formatPrizeTitle } from "@/utils/helpers";

import type { CampaignView } from "@/components/campaigns/campaign-shell/campaign-view";
import { FINISHED_DIM } from "@/components/campaigns/campaign-shell/campaign-view";
import {
  CampaignDescriptionText,
  EligibleGamesSection,
  HowItWorksCard,
  InfoSection,
  CampaignTimeLeftChip,
} from "@/components/campaigns/campaign-shell/campaign-sections";
import {
  CampaignJoinCta,
  CampaignJoinStickyBar,
} from "@/components/campaign-card/campaign-join-cta";
import { RaffleOverviewCard } from "@/components/campaigns/raffle/raffle-overview-card";
import { RaffleTasksCard } from "@/components/campaigns/raffle/raffle-tasks-card";
import { RafflePrizesCard } from "@/components/campaigns/raffle/raffle-prizes-card";
import { RaffleHomeCard } from "@/components/campaigns/raffle/raffle-home-card";
import { FinishedRaffleCard } from "@/components/campaign-card/finished-campaign/finished-raffle-card";

const raffleData = (current: CurrentCampaign): Raffle => current.data as Raffle;

function detailView(
  current: CurrentCampaign,
  t: TFunction
): CampaignView | null {
  if (current.type !== "raffle") return null;
  const raffle = raffleData(current);
  const { campaign } = raffle;
  const joined = current.joined;
  const phase = rafflePhase(raffle);
  // The campaign can still be opted into only while it is earning: an awaiting-draw raffle is
  // closed to entries even while the status flip is a push away.
  const canJoin = phase === "live" && canJoinCampaign(campaign, joined);
  const finished = phase !== "live";

  const howItWorks = (
    <HowItWorksCard
      text={t(
        "raffle.howItWorks",
        "Complete tasks to earn tickets. Every ticket is an entry in the draw, so the more you hold, the better your chances of winning a prize."
      )}
      minimumBet={raffle.minimum_bet}
    />
  );

  const overview = <RaffleOverviewCard raffle={raffle} joined={joined} />;
  const tasks = <RaffleTasksCard raffle={raffle} joined={joined} />;

  return {
    campaign,
    primaryLabel: t("nav.overview", "Overview"),
    finished,
    // On desktop the sidebar carries what the player does (how it works, the tasks) and the main
    // column what they stand to win; phones stack the tasks under the overview instead.
    sidebar: (
      <>
        {canJoin && (
          <CampaignJoinCta campaign={campaign} className="hidden lg:flex" />
        )}
        <AtLg>
          {finished ? (
            <div className={FINISHED_DIM}>{howItWorks}</div>
          ) : (
            howItWorks
          )}
        </AtLg>
        <AtLg>{tasks}</AtLg>
      </>
    ),
    guide: finished ? (
      <div className={FINISHED_DIM}>{howItWorks}</div>
    ) : (
      howItWorks
    ),
    // The overview card (balance + draw time, or the draw's result, with the prizes — and once
    // drawn, who won each — under it).
    primary: (
      <div className="flex w-full flex-col gap-2 lg:gap-4">
        {overview}
        <BelowLg>{tasks}</BelowLg>
      </div>
    ),
    landscape: {
      narrowSide: "start",
      narrow: (
        <RaffleOverviewCard raffle={raffle} joined={joined} prizes={false} />
      ),
      wide: (
        <>
          {tasks}
          <RafflePrizesCard raffle={raffle} />
        </>
      ),
    },
    info: (
      <div className="flex w-full flex-col gap-2 lg:gap-4">
        <InfoSection
          title={t("campaign.aboutHeading", "About")}
          aside={<CampaignTimeLeftChip campaign={campaign} />}
        >
          <CampaignDescriptionText text={campaign.campaign_info} />
        </InfoSection>
        <EligibleGamesSection
          games={raffle.eligible_games}
          count={raffle.eligible_games_count}
          campaignId={campaign.id}
        />
      </div>
    ),
    canJoin,
    mobileStickyAction: canJoin ? (
      <CampaignJoinStickyBar campaign={campaign} />
    ) : undefined,
  };
}

/** Raffles (`campaign.type === "RF"` — see `./assemble` for why it is an exact match). */
const raffleModule: CampaignTypeModule = {
  id: "raffle",
  providers: true,
  resolve(state: AppStore, key): CurrentCampaign | null {
    const raffle = selectRaffleById(state, key);
    if (!raffle) return null;
    return {
      type: "raffle",
      campaign: raffle.campaign,
      joined: selectJoined(state, key),
      data: raffle,
    };
  },
  listActive: (state) =>
    state.raffles
      .filter((r) => isActiveCampaignStatus(r.campaign.status))
      .map((r) => ({ campaign: r.campaign, data: r })),
  listFinished: (state) =>
    state.raffles
      .filter((r) => isFinishedCampaignStatus(r.campaign.status))
      .map((r) => ({ campaign: r.campaign, data: r })),
  hook: (entry, t) => {
    const raffle = entry.data as Raffle;
    // Prizes are place-ordered, so [0] is the top prize.
    const top = raffle.prizes[0];
    return top
      ? {
          label: t("campaign.topPrize", "Top prize"),
          value: formatPrizeTitle(top.title, t),
          isCatalogItem: top.is_catalog_item,
        }
      : null;
  },
  homeCard: (entry, layout) => (
    <RaffleHomeCard raffle={entry.data as Raffle} layout={layout} />
  ),
  finishedCard: (entry) => <FinishedRaffleCard raffle={entry.data as Raffle} />,
  detailView,
  launcher: {
    // Whether you opted in arrives with the progress stream: the coin waits for it (a campaign
    // without opt-in needn't).
    pending: (current, state) =>
      current.campaign.opt_in_method !== "NONE" &&
      !(campaignKey(current.campaign) in state.joinedById),
    // A drawn raffle keeps its place for its display window, finished.
    showsFinished: true,
    rows: (current, state, t) => {
      const raffle = raffleData(current);
      const phase = rafflePhase(raffle);
      if (phase === "drawn") {
        return { hero: t("igwButton.finished"), heroDisabled: true };
      }
      if (phase === "awaiting-draw") {
        return {
          hero: t("raffle.entriesClosed", "Entries closed"),
          heroFaded: true,
        };
      }
      if (
        canJoinCampaign(current.campaign, current.joined) &&
        campaignKey(current.campaign) in state.joinedById
      ) {
        return optInRows(current.campaign, t);
      }
      // `null` until the progress stream has pushed: the label alone then.
      const label = t("raffle.yourTickets", "Your tickets");
      return raffle.total_tickets !== null
        ? { hero: String(raffle.total_tickets), heroUnit: label }
        : { hero: label };
    },
  },
  // No overlay dock panel yet — the overlay shows its generic stand-in.
  dockPanel: () => null,
};

registerCampaignType(raffleModule);
